import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { differenceInMinutes, format } from 'date-fns';
import { collection, doc, getDoc, getDocs, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { BarChart3, CalendarDays, Download, Info, Loader2, Hash, Plus, ShieldAlert, Video, Zap, CheckCircle2, Lock, RefreshCw, Globe } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import ScheduleModal from '../components/ScheduleModal';
import SyncStatusBanner from '../components/SyncStatusBanner';
import { db } from '../firebase';
import { syncLessonToGoogle, importGoogleCalendarEvents } from '../lib/googleCalendar';
import { useTranslation } from '../lib/translations';
import { HomeworkAttachment, Lesson, Relationship, UserProfile } from '../types';

interface PlannerProps {
  profile: UserProfile | null;
}

const Planner: React.FC<PlannerProps> = ({ profile }) => {
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const { t, language } = useTranslation();
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'lesson' | 'block'>('lesson');
  const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [selectedTime, setSelectedTime] = useState<string>('14:00');
  const [isReadonly, setIsReadonly] = useState(false);
  
  // Edit State
  const [editingLessonId, setEditingLessonId] = useState<string | undefined>(undefined);
  const [editingGoogleEventId, setEditingGoogleEventId] = useState<string | undefined>(undefined);
  const [initialHomework, setInitialHomework] = useState<string>('');
  const [initialAttachments, setInitialAttachments] = useState<HomeworkAttachment[]>([]);
  const [initialDuration, setInitialDuration] = useState<number>(60);
  const [initialStudentId, setInitialStudentId] = useState<string | undefined>(undefined);
  const [initialMeetLink, setInitialMeetLink] = useState<string>('');
  
  const calendarRef = useRef<any>(null);

  const fetchData = async (silent = false) => {
    if (!profile) return;
    if (!silent) setLoading(true);
    try {
      const relSnap = await getDocs(query(collection(db, 'relationships'), where('teacherId', '==', profile.uid)));
      const rels = relSnap.docs.map(d => ({ id: d.id, ...d.data() } as Relationship));
      
      const enrichedRels = await Promise.all(rels.map(async r => {
        const isPending = r.studentId.startsWith('pending:');
        let email = '';
        let name = '';

        if (isPending) {
          email = r.studentId.split(':')[1];
          name = email.split('@')[0];
        } else {
          const userDoc = await getDoc(doc(db, 'users', r.studentId));
          const userData = userDoc.data();
          email = userData?.email || '';
          name = userData?.displayName || 'Unknown Student';
        }

        return { ...r, studentName: name, studentEmail: email };
      }));
      setRelationships(enrichedRels);

      const lessonSnap = await getDocs(query(collection(db, 'lessons'), where('teacherId', '==', profile.uid)));
      setLessons(lessonSnap.docs.map(d => ({ id: d.id, ...d.data() } as Lesson)));
    } catch (err) {
      console.error(err);
      setError(language === 'fr' ? 'Connexion à la base de données perdue.' : 'Connection to Studio database lost.');
    }
    if (!silent) setLoading(false);
  };

  useEffect(() => { fetchData(); }, [profile]);

  const handleSyncCalendar = async () => {
    if (!profile || isSyncing) return;
    setIsSyncing(true);
    try {
      const count = await importGoogleCalendarEvents(profile);
      if (count > 0) {
        await fetchData(true);
      }
    } catch (e) {
      console.error('Sync failed', e);
    }
    setIsSyncing(false);
  };

  const handleEventChange = async (changeInfo: any) => {
    const lessonId = changeInfo.event.id;
    const newStart = changeInfo.event.start;
    const newEnd = changeInfo.event.end;
    const lesson = lessons.find(l => l.id === lessonId);

    if (!lesson || !profile || lesson.status === 'completed') {
      changeInfo.revert();
      return;
    }

    try {
      const updatedLessonData: any = {
        start: Timestamp.fromDate(newStart),
        end: Timestamp.fromDate(newEnd)
      };

      const updatedLessonFull = {
        ...lesson,
        ...updatedLessonData
      };

      // Sync to Google
      if (profile.googleCalendarId) {
        const student = relationships.find(r => r.studentId === lesson.studentId);
        const result = await syncLessonToGoogle(updatedLessonFull, profile, student);
        
        if (result.id === 'RECREATE_REQUESTED') {
          const cleanLesson = { ...updatedLessonFull, googleEventId: undefined };
          const retry = await syncLessonToGoogle(cleanLesson, profile, student);
          if (retry.id) {
            updatedLessonData.googleEventId = retry.id;
            updatedLessonData.meetLink = retry.meetLink || null;
            updatedLessonFull.googleEventId = retry.id;
            updatedLessonFull.meetLink = retry.meetLink;
          }
        } else if (result.id) {
          updatedLessonData.googleEventId = result.id;
          updatedLessonData.meetLink = result.meetLink || null;
          updatedLessonFull.googleEventId = result.id;
          updatedLessonFull.meetLink = result.meetLink;
        }
      }

      await updateDoc(doc(db, 'lessons', lessonId), updatedLessonData);

      setLessons(prev => prev.map(l => 
        l.id === lessonId ? updatedLessonFull : l
      ));
    } catch (err) {
      console.error('Failed to reschedule lesson', err);
      alert(language === 'fr' ? "Erreur de synchronisation." : "Conflict detected or connection lost. Reschedule reverted.");
      changeInfo.revert();
    }
  };

  const handleDateSelect = (selectInfo: any) => {
    const start = selectInfo.start;
    const end = selectInfo.end;
    const diff = differenceInMinutes(end, start);

    setEditingLessonId(undefined);
    setEditingGoogleEventId(undefined);
    setInitialHomework('');
    setInitialAttachments([]);
    setInitialDuration(diff > 0 ? diff : 60);
    setInitialStudentId(undefined);
    setInitialMeetLink('');
    setIsReadonly(false);
    
    setSelectedDate(format(start, 'yyyy-MM-dd'));
    setSelectedTime(format(start, 'HH:mm'));
    setModalMode('lesson');
    setIsModalOpen(true);
  };

  const handleEventClick = (clickInfo: any) => {
    const lesson = lessons.find(l => l.id === clickInfo.event.id);
    if (!lesson) return;

    setEditingLessonId(lesson.id);
    setEditingGoogleEventId(lesson.googleEventId);
    setInitialHomework(lesson.homework?.description || '');
    setInitialAttachments(lesson.homework?.attachments || []);
    setInitialDuration(differenceInMinutes(lesson.end.toDate(), lesson.start.toDate()));
    setInitialStudentId(lesson.studentId);
    setInitialMeetLink(lesson.meetLink || '');
    setIsReadonly(lesson.status === 'completed');
    
    setSelectedDate(format(lesson.start.toDate(), 'yyyy-MM-dd'));
    setSelectedTime(format(lesson.start.toDate(), 'HH:mm'));
    setModalMode(lesson.type || 'lesson');
    setIsModalOpen(true);
  };

  const exportToICS = () => {
    if (lessons.length === 0) return;
    let icsContent = "BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//SharpNote//Music Lesson Schedule//EN\n";
    lessons.forEach(l => {
      if (l.status === 'canceled') return;
      const student = relationships.find(r => r.studentId === l.studentId);
      const isBlock = l.type === 'block';
      const start = l.start.toDate().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
      const end = l.end.toDate().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
      icsContent += "BEGIN:VEVENT\n";
      icsContent += `UID:${l.id}@sharpnote.app\n`;
      icsContent += `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").split(".")[0]}Z\n`;
      icsContent += `DTSTART:${start}\n`;
      icsContent += `DTEND:${end}\n`;
      icsContent += `SUMMARY:${isBlock ? (l.title || 'UNAVAILABLE') : (l.title || `Lesson: ${student?.studentName || 'Student'}`)}\n`;
      icsContent += `DESCRIPTION:${l.homework?.description || ''}${l.meetLink ? `\nJoin Meet: ${l.meetLink}` : ''}\n`;
      icsContent += "END:VEVENT\n";
    });
    icsContent += "END:VCALENDAR";
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute("download", `studio-schedule-${format(new Date(), 'yyyy-MM-dd')}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const calendarEvents = lessons.map(l => {
    const isBlock = l.type === 'block';
    const isCompleted = l.status === 'completed';
    const rel = relationships.find(r => r.studentId === l.studentId);
    
    // Transparent background for completed lessons, with white/dark border
    let bgColor = isBlock ? '#475569' : l.status === 'canceled' ? '#f1f5f9' : isCompleted ? 'transparent' : (rel?.color || '#9333ea');
    let borderColor = isBlock ? '#334155' : isCompleted ? 'transparent' : bgColor;
    // For completed lessons, text color should be responsive (black/white) but handled by Tailwind if bgColor is transparent
    const textColor = (isBlock || l.status === 'canceled') ? '#94a3b8' : isCompleted ? 'transparent' : '#ffffff';

    return {
      id: l.id,
      title: isBlock ? (l.title || 'UNAVAILABLE') : (l.title || rel?.studentName || 'Lesson'),
      start: l.start.toDate(),
      end: l.end.toDate(),
      backgroundColor: bgColor,
      borderColor: borderColor,
      textColor: textColor,
      editable: !isCompleted && l.status !== 'canceled', 
      extendedProps: {
        hasMeet: l.status === 'scheduled' && !!l.meetLink,
        status: l.status,
        isCompleted,
        source: l.source
      },
      classNames: [
        l.status === 'canceled' ? 'opacity-40 grayscale line-through' : 'shadow-lg',
        isCompleted 
          ? 'border-2 border-slate-900 dark:border-white bg-transparent opacity-80 cursor-pointer' 
          : 'cursor-pointer hover:scale-[1.02] transition-transform',
      ],
      display: 'block'
    };
  });

  const renderEventContent = (eventInfo: any) => {
    const { isCompleted, hasMeet, source } = eventInfo.event.extendedProps;
    return (
      <div className={`flex flex-col justify-center h-full px-1.5 overflow-hidden leading-tight py-0.5 ${isCompleted ? 'text-slate-900 dark:text-white' : ''}`}>
        <div className="flex items-center justify-between gap-1 mb-0.5">
          <div className="flex items-center gap-1 min-w-0">
            {isCompleted ? (
              <CheckCircle2 size={10} className="text-teal-600 shrink-0" />
            ) : null}
            {source === 'google_calendar' && !isCompleted && (
              <Globe size={10} className="text-white shrink-0 opacity-80" />
            )}
            <div className={`truncate font-black text-[10px] uppercase tracking-tighter leading-none ${isCompleted ? 'text-slate-900 dark:text-white' : ''}`}>
              {eventInfo.event.title}
            </div>
          </div>
          {hasMeet && (
            <Video size={10} className="shrink-0 opacity-80" />
          )}
          {isCompleted && (
             <Lock size={8} className="shrink-0 opacity-60 ml-auto" />
          )}
        </div>
        <div className={`text-[9px] font-bold opacity-90 tracking-tighter whitespace-nowrap overflow-hidden leading-none ${isCompleted ? 'text-slate-600 dark:text-slate-300' : ''}`}>
          {eventInfo.timeText}
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="flex flex-col justify-center items-center h-[70vh] gap-6">
      <div className="relative">
        <Loader2 className="animate-spin text-purple-600" size={56} strokeWidth={1.5} />
        <Hash size={20} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-purple-600" />
      </div>
      <div className="text-center">
        <p className="text-slate-900 dark:text-white font-black text-xs uppercase tracking-[0.3em] mb-1">{t.common.loading}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-12 max-w-7xl mx-auto pb-32 px-4 md:px-0">
      <SyncStatusBanner profile={profile} onReconnectSuccess={fetchData} />

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 text-[10px] font-black uppercase tracking-widest shadow-sm">
            <Zap size={12} className="animate-pulse" /> {t.planner.engine}
          </div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter">{t.planner.title}</h1>
          <p className="text-slate-500 font-medium">{t.planner.desc}</p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <button onClick={exportToICS} className="flex items-center gap-2 px-6 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-lg font-bold text-xs text-slate-600 dark:text-slate-300 hover:border-purple-500/30 transition-all shadow-sm">
            <Download size={16} /> {t.planner.export}
          </button>
          
          <button 
            onClick={handleSyncCalendar} 
            disabled={isSyncing || !profile?.googleCalendarSyncEnabled}
            className={`flex items-center gap-2 px-6 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-lg font-bold text-xs text-slate-600 dark:text-slate-300 hover:border-purple-500/30 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <RefreshCw size={16} className={isSyncing ? 'animate-spin' : ''} /> 
            {isSyncing ? t.planner.syncing : t.planner.syncAction}
          </button>

          <div className="flex items-center gap-3">
            <button onClick={() => { 
              setEditingLessonId(undefined);
              setModalMode('block'); 
              setIsModalOpen(true); 
              setIsReadonly(false);
            }} className="flex items-center gap-2 px-6 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-white/5 rounded-lg font-bold text-xs transition-all hover:bg-slate-200 dark:hover:bg-slate-700">
              <ShieldAlert size={16} className="text-slate-400" /> {t.planner.blockTimeAction}
            </button>
            <button onClick={() => { 
              setEditingLessonId(undefined);
              setModalMode('lesson'); 
              setIsModalOpen(true); 
              setIsReadonly(false);
            }} className="flex items-center gap-2 px-8 py-2.5 bg-purple-600 text-white rounded-lg font-black text-xs uppercase tracking-widest shadow-xl shadow-purple-500/20 hover:scale-105 active:scale-95 transition-all">
              <Plus size={16} /> {t.planner.newSession}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl p-8 shadow-2xl overflow-hidden relative">
        <div className="calendar-container">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            locale={language}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            buttonText={{
              today: t.common.today,
              month: t.common.month,
              week: t.common.week,
              day: t.common.day
            }}
            events={calendarEvents}
            editable={true}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            weekends={true}
            nowIndicator={true}
            slotMinTime="08:00:00"
            slotMaxTime="22:00:00"
            allDaySlot={false}
            eventChange={handleEventChange}
            select={handleDateSelect}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            height="auto"
          />
        </div>
      </div>

      <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-white/5 rounded-xl p-8 flex flex-col md:flex-row items-center gap-6">
        <div className="w-14 h-14 bg-white dark:bg-slate-900 rounded-lg flex items-center justify-center shadow-lg">
          <Info size={24} className="text-purple-600" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h4 className="text-lg font-black text-slate-900 dark:text-white mb-1">{t.planner.tips.title}</h4>
          <p className="text-sm text-slate-500 font-medium">{t.planner.tips.desc}</p>
        </div>
        <div className="flex flex-wrap justify-center md:justify-end gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-600"></div>
            <span className="text-[10px] font-black uppercase text-slate-400">{t.planner.legend.lesson}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full border-2 border-slate-900 dark:border-white bg-transparent"></div>
            <span className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1">
               <CheckCircle2 size={10} className="text-[#10b981]" /> {t.common.completed}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-slate-600"></div>
            <span className="text-[10px] font-black uppercase text-slate-400">{t.planner.legend.blocked}</span>
          </div>
          <div className="flex items-center gap-2">
            <Globe size={12} className="text-slate-400" />
            <span className="text-[10px] font-black uppercase text-slate-400">Google Sync</span>
          </div>
        </div>
      </div>

      <ScheduleModal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); fetchData(); }} 
        profile={profile} 
        lessonId={editingLessonId}
        googleEventId={editingGoogleEventId}
        initialStudentId={initialStudentId}
        initialHomework={initialHomework}
        initialAttachments={initialAttachments}
        initialType={modalMode}
        initialDate={selectedDate}
        initialTime={selectedTime}
        initialDuration={initialDuration}
        initialMeetLink={initialMeetLink}
        readonly={isReadonly}
      />
    </div>
  );
};

export default Planner;